import { useCallback, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowDownTrayIcon,
  ClipboardDocumentIcon,
  Cog6ToothIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import { useToast } from '../../contexts/ToastContext';
import { useFlag } from '../../hooks/useFeatureFlags';
import { formatRunCost } from '../../utils/formatUtils';
import { UserName } from '../ui/UserName';
import type { RunDetail } from '../../types/runExplorer';
import type { RunSummary } from '../../utils/runTurnModel';
import {
  CopyButton,
  formatClock,
  formatCount,
  formatMs,
  getStatusBadgeClass,
  runErrorText,
} from './runExplorerShared';
import { runExplorerPath } from './runExplorerLinks';

interface RunHeaderProps {
  detail: RunDetail;
  runId: string;
  turnCount: number;
  summary: RunSummary;
  /** First turn's start and last turn's end, from the turn model. */
  span: { startedAt: string | null; endedAt: string | null };
  /** Page-chrome badges (test and schedule provenance on the AI Service run route). */
  badges?: ReactNode;
}

/** Whole-number share of the run's model-call input read from the prompt cache. */
const cacheReadShare = (summary: RunSummary): number | null =>
  summary.inputTokens > 0 && (summary.cacheReadTokens > 0 || summary.cacheWriteTokens > 0)
    ? Math.round((summary.cacheReadTokens / summary.inputTokens) * 100)
    : null;

function Tile({ label, value, sub, tone, testId }: { label: string; value: ReactNode; sub?: ReactNode; tone?: 'amber'; testId?: string }) {
  return (
    <div
      data-testid={testId}
      className="min-w-0 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-3 py-2"
    >
      <div className="text-[11px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">{label}</div>
      <div className={`text-lg font-semibold ${tone === 'amber' ? 'text-amber-700 dark:text-amber-300' : 'text-gray-900 dark:text-white'}`}>
        {value}
      </div>
      {sub && <div className="text-xs text-gray-500 dark:text-gray-400 truncate">{sub}</div>}
    </div>
  );
}

/**
 * The run's header (task 1151, steps 5 and 12): who and what ran, the actions,
 * a "Started by" chip for a child run, and the summary strip across all turns.
 *
 * Carries forward two pieces of the page it replaced: the run's failure
 * sentence (task 1145) sits under the status line, and the cache tile's read
 * share (task 1126) sits in the Tokens tile, now summed over every model call
 * rather than the first hundred.
 */
export function RunHeader({ detail, runId, turnCount, summary, span, badges }: RunHeaderProps) {
  const toast = useToast();
  const collaboration = detail.collaboration;
  const run = { ...collaboration, ...detail.run };
  const status = run.status ?? 'unknown';
  const config = collaboration.collaborationConfig;
  const configId = config?.id ?? collaboration.configId;
  const title = config?.name ?? 'Run';
  const threadId = run.threadId ?? null;
  const share = cacheReadShare(summary);
  // Per-run cost (task 1253): the service's price of the run's journal usage;
  // the tile is omitted when the flag is off or the run could not be priced.
  const costEnabled = useFlag('innovate.fe.analytics.per-run-cost');
  const cost = costEnabled ? detail.cost ?? null : null;

  const handleCopyId = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(runId);
      toast.success('Run ID copied');
    } catch {
      toast.error('Failed to copy');
    }
  }, [runId, toast]);

  const handleExport = useCallback(() => {
    const blob = new Blob([JSON.stringify(detail, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `run-${runId}.json`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  }, [detail, runId]);

  const buttonClass =
    'inline-flex items-center gap-1.5 px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700';

  return (
    <div className="space-y-4" data-testid="run-header">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0">
          <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">
            {title}
            {config?.version && (
              <span className="ml-2 text-base font-normal text-gray-500 dark:text-gray-400">v{config.version}</span>
            )}
          </h1>
          <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-gray-600 dark:text-gray-300">
            <span data-testid="run-status" className={`px-2 py-0.5 rounded-full text-xs font-medium ${getStatusBadgeClass(status)}`}>
              {status}
            </span>
            {/* The test that produced the run (task 1306), from the run itself. */}
            {detail.test && (
              <span
                data-testid="run-test-chip"
                title={`Produced by test ${detail.test.scenarioName}`}
                className="px-2 py-0.5 rounded-full text-xs font-medium bg-teal-100 text-teal-800 dark:bg-teal-900 dark:text-teal-200"
              >
                Test
              </span>
            )}
            {threadId && (
              <span>
                thread · {turnCount} {turnCount === 1 ? 'turn' : 'turns'}
              </span>
            )}
            {badges}
            {detail.parent && (
              <span
                data-testid="run-started-by"
                className="px-2 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800 dark:bg-indigo-900 dark:text-indigo-200"
              >
                Started by {detail.parent.collaborationName ?? 'another collaboration'} ·{' '}
                <Link
                  to={runExplorerPath(detail.parent.collaborationId, detail.parent.runId)}
                  className="underline"
                >
                  run
                </Link>
              </span>
            )}
          </div>
          <dl className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-500 dark:text-gray-400">
            <div className="flex items-center gap-1">
              <dt>Run</dt>
              <dd className="font-mono">{runId.slice(0, 8)}</dd>
              <CopyButton text={runId} label="Run ID" />
            </div>
            {run.requestId && (
              <div className="flex items-center gap-1">
                <dt>Request</dt>
                <dd className="font-mono">{run.requestId.slice(0, 8)}</dd>
                <CopyButton text={run.requestId} label="Request ID" />
              </div>
            )}
            {threadId && (
              <div className="flex items-center gap-1">
                <dt>Thread</dt>
                <dd className="font-mono">{threadId.slice(0, 8)}</dd>
                <CopyButton text={threadId} label="Thread ID" />
              </div>
            )}
            {run.createdBy && (
              <div className="flex items-center gap-1">
                <dt>Run by</dt>
                <dd>
                  <UserName userId={run.createdBy} />
                </dd>
              </div>
            )}
            <div className="flex items-center gap-1">
              <dt>Started</dt>
              <dd>{formatClock(span.startedAt ?? run.startedAt ?? null)}</dd>
            </div>
            {span.endedAt && run.completedAt && (
              <div className="flex items-center gap-1">
                <dt>Completed</dt>
                <dd>{formatClock(span.endedAt)}</dd>
              </div>
            )}
            <div className="flex items-center gap-1">
              <dt>Wall time</dt>
              <dd data-testid="run-wall-time">{formatMs(summary.wallMs)}</dd>
            </div>
          </dl>
        </div>
        <div className="flex flex-wrap gap-2">
          <button type="button" onClick={handleCopyId} className={buttonClass}>
            <ClipboardDocumentIcon className="h-4 w-4" />
            Copy Run ID
          </button>
          <button type="button" onClick={handleExport} className={buttonClass}>
            <ArrowDownTrayIcon className="h-4 w-4" />
            Export run (JSON)
          </button>
          {configId && (
            <Link to={`/configs/${configId}`} className={buttonClass}>
              <Cog6ToothIcon className="h-4 w-4" />
              Open config
            </Link>
          )}
        </div>
      </div>

      {run.error && (
        <div
          data-testid="run-failure"
          className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg flex items-start gap-3"
        >
          <ExclamationTriangleIcon className="h-5 w-5 text-red-500 dark:text-red-400 flex-shrink-0 mt-0.5" />
          <div>
            <h3 className="text-sm font-medium text-red-800 dark:text-red-200">Run failed</h3>
            <p className="mt-1 text-sm text-red-700 dark:text-red-300">{runErrorText(run.error)}</p>
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 sm:grid-cols-4 xl:grid-cols-8 gap-2" data-testid="run-summary-strip">
        <Tile
          testId="summary-steps"
          label="Steps run"
          value={`${summary.stepsRan} of ${summary.stepsTotal}`}
          sub={`${summary.stepsSkipped} skipped by routing`}
        />
        <Tile
          testId="summary-model-calls"
          label="Model calls"
          value={summary.modelCalls}
          sub={summary.problemCalls > 0 ? `${summary.problemCalls} with a problem` : 'no problems'}
          tone={summary.problemCalls > 0 ? 'amber' : undefined}
        />
        <Tile testId="summary-tool-calls" label="Tool calls" value={summary.toolCalls} />
        <Tile
          testId="summary-tokens"
          label="Tokens"
          value={formatCount(summary.inputTokens + summary.outputTokens)}
          sub={
            <>
              in {formatCount(summary.inputTokens)} · out {formatCount(summary.outputTokens)}
              {share !== null && (
                <span data-testid="run-cache-share" className="text-blue-700 dark:text-blue-300">
                  {' '}
                  · {share}% read from cache
                </span>
              )}
            </>
          }
        />
        {cost && (
          <Tile
            testId="summary-cost"
            label="Cost"
            value={formatRunCost(cost)}
            sub="journal usage at Model catalog prices"
          />
        )}
        <Tile
          testId="summary-model-time"
          label="Model time"
          value={formatMs(summary.modelTimeMs)}
          sub={`of ${formatMs(summary.wallMs)} wall`}
        />
        <Tile testId="summary-child-runs" label="Child runs" value={summary.childRuns} />
        <Tile
          testId="summary-artifacts"
          label="Artifacts created"
          value={summary.artifacts.length}
          sub={summary.artifacts.length ? summary.artifacts.join(' · ') : 'nothing'}
        />
      </div>
    </div>
  );
}
